import { FilterValue } from '../types/index';
import { decodeData } from '../../../utils/encodeUtils';
import { decodeMappingStateFromUrl, MAPPING_URL_PARAM } from '../../../utils/mappingUrlState';
import Filters from '../Filters';

export const getMappingFiltersFromURL = (urlParams: URLSearchParams): FilterValue[] => {
  const encodedMapping = urlParams.get(MAPPING_URL_PARAM);
  const mappingFilters: FilterValue[] = [];
  if (!encodedMapping) return mappingFilters;

  const decodedMapping = decodeMappingStateFromUrl(encodedMapping);
  const currentLevel = decodedMapping?.stack?.[decodedMapping.currentIndex ?? 0];
  const mappingContent = currentLevel?.mappingContent as
    | {
        selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL';
        selectedPmYear?: number;
        selectedPolarity?: 'Positive' | 'Negative';
      }
    | undefined;

  if (mappingContent?.selectedReferenceType === 'PMID' || mappingContent?.selectedReferenceType === 'NCTID') {
    mappingFilters.push({
      name: 'reference_type',
      label: 'Reference type',
      operator: 'eq',
      type: 'select',
      value: [mappingContent.selectedReferenceType],
    } as FilterValue);
  }

  if (mappingContent?.selectedPolarity === 'Positive' || mappingContent?.selectedPolarity === 'Negative') {
    mappingFilters.push({
      name: 'association',
      label: 'Polarity',
      operator: 'eq',
      type: 'select',
      value: [mappingContent.selectedPolarity],
    } as FilterValue);
  }

  if (mappingContent?.selectedPmYear != null && Number.isFinite(Number(mappingContent.selectedPmYear))) {
    mappingFilters.push({
      name: 'pmYear',
      label: 'PM year',
      operator: 'eq',
      type: 'number',
      integer: true,
      value: String(Number(mappingContent.selectedPmYear)),
    } as FilterValue);
  }

  return mappingFilters;
};

export const initializeFiltersFromURL = (restDefaultFilterValue?: FilterValue[]): FilterValue[] => {
  const urlParams = new URLSearchParams(window.location.search);
  const encodedFilters = urlParams.get('f');

  let urlFilters: FilterValue[] = [];
  if (encodedFilters) {
    try {
      urlFilters = decodeData(encodedFilters);
    } catch {
      // Silent error handling - use empty array if decoding fails
    }
  }

  const baseFilters = Filters.filter((f: any) => f).map((f: any) => ({ ...f, value: f.value || '' }));
  const propFilters = Array.isArray(restDefaultFilterValue) ? restDefaultFilterValue : [];
  const mappingFilters = getMappingFiltersFromURL(urlParams);

  const mergeByName = (filters: FilterValue[]): FilterValue[] => {
    const merged = new Map<string, FilterValue>();
    filters.forEach(f => {
      if (f?.name) merged.set(f.name, { ...f, value: f.value || '' });
    });
    return Array.from(merged.values());
  };

  if (urlFilters.length > 0) {
    return mergeByName([...baseFilters, ...urlFilters, ...propFilters, ...mappingFilters]);
  }

  if (propFilters.length > 0 || mappingFilters.length > 0) {
    return mergeByName([...baseFilters, ...propFilters, ...mappingFilters]);
  }

  return baseFilters;
};

export const shouldShowRestrictionError = (
  restrictGridDisplay: boolean,
  restrictChemical: boolean,
  view: string,
  tab: string,
  isMapping?: boolean,
  mappingContent?: any,
): boolean => {
  return (
    (restrictGridDisplay && view === 'ALL') ||
    (restrictChemical && tab === 'CHEMICALS') ||
    (restrictChemical && view === 'CHEMICALS') ||
    (restrictChemical && isMapping && mappingContent?.tabSegment.includes('chem')) ||
    (restrictChemical && isMapping && mappingContent?.name === 'numberPmidsWithChemsIncludedInEvidence')
  );
};

// Re-export functions from SortFilterTables
export {
  getFiltersMap,
  getFilters,
  getSort,
  getEmptinessFilterValue,
  getMinFilterString,
  getMaxFilterString,
  getNumberFilter,
} from './SortFilterTables';

export function getSelectedRowsFromEnrichmentState<T = any>(
  rowData: T[],
  enrichmentRowSelection: Record<string, boolean>,
  getRowId: ((row: T, index: number) => string) | undefined,
): T[] {
  const getKey = getRowId ?? ((_: T, i: number) => String(i));
  return rowData.filter((row, index) => enrichmentRowSelection[getKey(row, index)]);
}

export function buildEnrichmentTableRowId(logicalId: string, skip: number, index: number): string {
  const safeLogical = logicalId?.trim() ? logicalId.trim() : 'row';
  return `${safeLogical}__${skip}_${index}`;
}

export function getLogicalEnrichmentIdFromTableRowId(tableRowId: string): string {
  return tableRowId.replace(/__\d+_\d+$/, '');
}

export function countUniqueEnrichmentLogicalIds(selectedTableRowIds: string[]): number {
  const unique = new Set(
    selectedTableRowIds.map(getLogicalEnrichmentIdFromTableRowId).filter(id => id.length > 0 && id !== 'row'),
  );
  return unique.size;
}

export function getUniqueEnrichmentExportIds<T>(rows: T[], getExportId: (row: T) => string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const row of rows) {
    const id = getExportId(row)?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

export function countUniqueEnrichmentExportIds<T>(rows: T[], getExportId: (row: T) => string): number {
  return getUniqueEnrichmentExportIds(rows, getExportId).length;
}

export function dedupeRowsByEnrichmentLogicalId<T>(rows: T[], getLogicalId: (row: T) => string): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const row of rows) {
    const logicalId = getLogicalId(row)?.trim();
    if (!logicalId || seen.has(logicalId)) continue;
    seen.add(logicalId);
    result.push(row);
  }
  return result;
}

export function isRunEnrichmentEnabled(selectedCount: number): boolean {
  return selectedCount > 1;
}
